// EditPersonContactsModal.tsx

import { Button, Divider, Input, message, Modal, Tooltip } from "antd";
import { useState } from "react";
import { TbMail, TbPhone, TbPlus, TbTrash } from "react-icons/tb";
import { IPerson } from "dtos/communication-channel.dto";
import { ApiService } from "api/api.service";
import { useQueryClient } from "@tanstack/react-query";
import { formatPhoneDisplay } from "utils/functions";

// ─── Types ────────────────────────────────────────────────────────────────────

type T_Props = {
  open: boolean;
  onClose: () => void;
  person?: IPerson;
};

const SECTION_LABEL: React.CSSProperties = {
  display: "block",
  marginBottom: 8,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#94a3b8",
};

// ─── Sub-component: existing entry row ───────────────────────────────────────

function ExistingEntry({
  value,
  type,
  staged,
  onToggle,
  isLast,
}: {
  value: string;
  type: "email" | "phone";
  staged: boolean;
  onToggle: () => void;
  isLast: boolean;
}) {
  const Icon = type === "email" ? TbMail : TbPhone;
  const disableRemove = isLast && !staged;

  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
      style={{
        background: staged ? "#fff1f0" : "#f8fafc",
        border: `1px solid ${staged ? "#fca5a5" : "#e2e8f0"}`,
        transition: "all 0.15s",
      }}
    >
      <Icon size={12} style={{ color: staged ? "#f87171" : "#94a3b8", flexShrink: 0 }} />
      <span
        style={{
          fontSize: 12.5,
          color: staged ? "#ef4444" : "#374151",
          flex: 1,
          textDecoration: staged ? "line-through" : "none",
        }}
      >
        {type === "phone" ? formatPhoneDisplay(value) : value}
      </span>
      <Tooltip title={disableRemove ? `At least one ${type} is required` : staged ? "Undo remove" : "Remove"}>
        <Button
          type="text"
          size="small"
          danger={!staged}
          icon={<TbTrash size={13} />}
          onClick={onToggle}
          disabled={disableRemove}
          style={{ color: staged ? "#94a3b8" : undefined }}
        />
      </Tooltip>
    </div>
  );
}

// ─── Sub-component: channel section ──────────────────────────────────────────

function ChannelSection({
  type,
  existing,
  stagedForRemoval,
  newEntries,
  onToggleRemove,
  onAdd,
  onUpdate,
  onRemoveNew,
}: {
  type: "email" | "phone";
  existing: string[];
  stagedForRemoval: string[];
  newEntries: string[];
  onToggleRemove: (value: string) => void;
  onAdd: () => void;
  onUpdate: (index: number, value: string) => void;
  onRemoveNew: (index: number) => void;
}) {
  const isEmail = type === "email";
  const Icon = isEmail ? TbMail : TbPhone;
  const placeholder = isEmail ? "email@example.com" : "+1 234 567 8900";
  const label = isEmail ? "Email Addresses" : "Phone Numbers";
  const addLabel = isEmail ? "Add email address" : "Add phone number";

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={13} style={{ color: "#6366f1" }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{label}</span>
      </div>

      {existing.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-3">
          <span style={SECTION_LABEL}>Existing</span>
          {existing.map((entry, i) => (
            <ExistingEntry
              key={i}
              value={entry}
              type={type}
              staged={stagedForRemoval.includes(entry)}
              onToggle={() => onToggleRemove(entry)}
              isLast={existing.length === 1}
            />
          ))}
        </div>
      )}

      {newEntries.length > 0 && (
        <div className="flex flex-col gap-2 mb-2">
          <span style={SECTION_LABEL}>New</span>
          {newEntries.map((entry, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                autoFocus={i === newEntries.length - 1}
                placeholder={placeholder}
                prefix={<Icon size={13} className="text-gray-400" />}
                value={entry}
                onChange={(e) => onUpdate(i, e.target.value)}
                onPressEnter={onAdd}
                style={{ flex: 1 }}
              />
              <Button type="text" danger icon={<TbTrash size={14} />} onClick={() => onRemoveNew(i)} />
            </div>
          ))}
        </div>
      )}

      <Button type="dashed" block icon={<TbPlus size={13} />} onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const EditPersonContactsModal: React.FC<T_Props> = ({ open, onClose, person }) => {
  const [newEmails, setNewEmails] = useState<string[]>([]);
  const [newNumbers, setNewNumbers] = useState<string[]>([]);
  const [emailsToRemove, setEmailsToRemove] = useState<string[]>([]);
  const [numbersToRemove, setNumbersToRemove] = useState<string[]>([]);

  const qc = useQueryClient();
  const addContacts = ApiService.communicationChannel.useUpdatePersonContacts();
  const removeContacts = ApiService.communicationChannel.useRemovePersonContacts();

  const makeNewHandlers = (setter: React.Dispatch<React.SetStateAction<string[]>>) => ({
    onAdd: () => setter((p) => [...p, ""]),
    onUpdate: (i: number, val: string) => setter((p) => p.map((e, j) => (j === i ? val : e))),
    onRemoveNew: (i: number) => setter((p) => p.filter((_, j) => j !== i)),
  });

  const toggleRemove = (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const emailHandlers = makeNewHandlers(setNewEmails);
  const numberHandlers = makeNewHandlers(setNewNumbers);

  const filledEmails = newEmails.filter((e) => e.trim() !== "");
  const filledNumbers = newNumbers.filter((n) => n.trim() !== "");

  const hasAdditions = filledEmails.length > 0 || filledNumbers.length > 0;
  const hasRemovals = emailsToRemove.length > 0 || numbersToRemove.length > 0;
  const hasChanges = hasAdditions || hasRemovals;

  const isSaving = addContacts.isPending || removeContacts.isPending;

  const handleClose = () => {
    setNewEmails([]);
    setNewNumbers([]);
    setEmailsToRemove([]);
    setNumbersToRemove([]);
    onClose();
  };

  const handleSave = async () => {
    const tasks: Promise<unknown>[] = [];

    if (hasAdditions) {
      tasks.push(
        addContacts.mutateAsync({
          person_id: person?._id as string,
          body: { emails: filledEmails, numbers: filledNumbers },
        }),
      );
    }

    if (hasRemovals) {
      tasks.push(
        removeContacts.mutateAsync({
          person_id: person?._id as string,
          body: { emails: emailsToRemove, numbers: numbersToRemove },
        }),
      );
    }

    const results = await Promise.allSettled(tasks);

    const failed = results.filter((r) => r.status === "rejected");
    const succeeded = results.filter((r) => r.status === "fulfilled");

    if (succeeded.length > 0) {
      qc.invalidateQueries({ queryKey: ["persons-details", person?._id] });
    }

    if (failed.length === 0) {
      message.success("Contacts updated successfully");
      handleClose();
    } else if (succeeded.length > 0) {
      message.warning("Some changes were saved but others failed. Please review and retry.");
    } else {
      message.error("Failed to update contacts. Please try again.");
    }
  };

  return (
    <Modal open={open} centered onCancel={handleClose} footer={null} title="Edit Contacts" width={560} destroyOnClose>
      <div className="max-h-[500px] overflow-auto">
        <ChannelSection
          type="email"
          existing={person?.emails ?? []}
          stagedForRemoval={emailsToRemove}
          newEntries={newEmails}
          onToggleRemove={(val) => toggleRemove(setEmailsToRemove, val)}
          {...emailHandlers}
        />

        <Divider style={{ margin: "16px 0" }} />

        <ChannelSection
          type="phone"
          existing={person?.numbers ?? []}
          stagedForRemoval={numbersToRemove}
          newEntries={newNumbers}
          onToggleRemove={(val) => toggleRemove(setNumbersToRemove, val)}
          {...numberHandlers}
        />
      </div>

      <div
        className="flex items-center justify-end gap-2"
        style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}
      >
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="primary" disabled={!hasChanges} loading={isSaving} onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </Modal>
  );
};

export default EditPersonContactsModal;
